import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import Shape from '../../@types/Shape';
import Form from '../common/Form/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider } from 'react-hook-form';
import { Stack } from '@mui/material';
import TextField from '../common/Form/FormFields/TextField/TextField';
import { ZendeskSubmitRequest } from '../../@types/Zendesk';
import axios from 'axios';
import LoadingButton from '../common/LoadingButton/LoadingButton';
import useAuth from '../../hooks/useAuth';
import flatten from 'flat';

interface ContactUs {
  dealerId: string,
  companyName: string,
  email: string,
  message: string
}

interface ContactUsFormProps { closeModal: () => void; }

const ContactUsForm: FC<ContactUsFormProps> = ({ closeModal }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (data: ContactUs) => {
    try {
      setLoading(true);
      const zendeskRequest: ZendeskSubmitRequest = {
        request: {
          requester: { email: data.email },
          subject: 'Dealer Portal [Contact Us]',
          comment: {
            body: data.message
          },
          tags: ["madjax-xseries", "dealer-portal", "contact-us"],
          custom_fields: [
            {
              "id": 14985663314199,
              "value": "data.dealerInformation.dealerId"
            },
            {
              "id": 4855366229783,
              "value": "data.dealerInformation.dealerName"
            },
            {
              "id": 14987674192279,
              "value": "serialNumbersList"
            }
          ],
          organization_id: "15578145019287"
        }
      }
      const authHeaders = { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_ZENDESK_API_KEY };
      await axios.post(`${process.env.REACT_APP_ZENDESK_API}/requests`, zendeskRequest, { headers: authHeaders });
      closeModal()
      toast.success('A support representative will be in contact with your shortly');
    } catch (error) {
      console.log(error);
      toast.error('Server Error');
    } finally {
      setLoading(false);
    }
  }

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  }

  const contactUsSchema = yup.object<Shape<ContactUs>>({
    dealerId: yup.string().required(),
    companyName: yup.string().required(),
    email: yup.string()
      .strict()
      .trim('email address cannot include leading or trailing spaces')
      .email().email('Please enter a valid email address')
      .required(`Please enter an email address`),
    message: yup.string().required()
  });

  const defaultValues = {
    dealerId: user?.companyAttributes?.dealerId,
    companyName: user?.companyAttributes?.companyName,
    email: user?.email
  }

  return (
    <Form<ContactUs> onSubmit={onSubmit} onError={onError} resolver={yupResolver(contactUsSchema)} defaultValues={defaultValues}>
      {(methods) => (
        <FormProvider {...methods}>
          <Stack direction="column" spacing={4}>
            <TextField name="dealerId" label="Dealer ID" />
            <TextField name="companyName" label="Company" />
            <TextField name="email" label="Email Contact" />
            <TextField name="message" label="Message" multiline maxLength={500} rows={6} placeholder="Type your message here..." />
            <LoadingButton loading={loading} type="submit">Submit</LoadingButton>
          </Stack>
        </FormProvider>
      )}
    </Form>
  );
}

export default ContactUsForm;
